import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import { CustomInput, FormText } from 'reactstrap'
import { CurrencyInputField, LoaderButton } from 'components/molecules'
import PriceUtil from 'utils/price-util'
import MarketplaceProductsRevenuesApi from 'api/v2/admins/marketplace/products/marketplace-products-revenues-api'
import sellerproductConvertToMarketProductFormInitialValues from './seller-product-convert-to-market-product-form-initial-values'
import sellerproductConvertToMarketProductFormSchema from './seller-product-convert-to-market-product-form-schema'
import { CONVERT_TO_MP_MINIMUM_PRICE } from 'domains/seller-product/constants'
import ConsignmentProductsApi from 'api/admins/consignment-products-api'

const SellerRevenueExample = ({ revenues }) => (
  <dl style={{ backgroundColor: '#F8F8F8' }} className='row small rounded p-2'>
    <dt className='col-12 pb-2'>{ 'Contoh' }</dt>
    <dt className='col-7 font-weight-normal'>{ 'Harga penawaran Kamu:' }</dt>
    <dd className='col-5 font-weight-normal text-nowrap'>{ PriceUtil.formatToIDR(revenues.seller_revenue, true, 'IDR') }</dd>

    <dt className='col-7 font-weight-normal'>{ 'Harga jual yang dipublish:' }</dt>
    <dd className='col-5 font-weight-normal text-nowrap'>{ PriceUtil.formatToIDR(revenues.selling_price, true, 'IDR') }</dd>

    <dt className='col-7 font-weight-normal'>{ 'Komisi Tinkerlust:' }</dt>
    <dd className='col-5 font-weight-normal text-nowrap text-muted'>{ '- ' } { PriceUtil.formatToIDR(revenues.commission_fee, true, 'IDR') }</dd>

    <dt className='col-7 font-weight-normal border-top pt-2 font-weight-bold'>{ 'Revenue yang diterima:' }</dt>
    <dd className='col-5 font-weight-normal text-nowrap border-top pt-2 font-weight-bold'>{ PriceUtil.formatToIDR(revenues.seller_revenue, true, 'IDR') }</dd>
  </dl>
)

SellerRevenueExample.propTypes = { revenues: PropTypes.object }

const propTypes = {
  product   : PropTypes.object,
  onSubmit  : PropTypes.func,
  pageUtils : PropTypes.object
}

const defaultProps = {
  product   : {},
  onSubmit  : () => { },
  pageUtils : {}
}

const SellerProductConvertToMarketProductForm = ({
  product,
  onSubmit,
  pageUtils
}) => {

  const [ isAgreementChecked, setIsAgreementChecked ]     = useState(false)
  const [ revenues, setRevenues ]                         = useState({})
  const [ brandClassifications, setBrandClassifications ] = useState('')

  let userSearchTimeout = null

  const getRevenues = (inputPrice) => {
    const isUnderMinimumPrice = inputPrice < CONVERT_TO_MP_MINIMUM_PRICE

    if (isUnderMinimumPrice) return

    MarketplaceProductsRevenuesApi.get({
      params: {
        price : inputPrice,
        plan  : 'cut'
      }
    })
      .then((response) => setRevenues(response.data.revenues))
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  // Getting revenue information to be displayed in TnC
  useEffect(() => {
    const price = product?.price
    getRevenues(price)
    getBrandDetails()
  }, [ product ])  // eslint-disable-line react-hooks/exhaustive-deps

  const handlePriceChange = (inputPrice) => {
    const price = inputPrice
    if (userSearchTimeout) clearTimeout(userSearchTimeout)
    userSearchTimeout = setTimeout(() => {
      getRevenues(price)
    }, 700)
  }

  const getBrandDetails = useCallback(() => {
    const brand = product.seller_product.product_attributes?.brand
    ConsignmentProductsApi.showBrand(brand)
      .then((response) => {
        const classifications             = response.data.brand.classifications
        const productBrandClassifications = classifications.map((classification) => (classification.name))
        setBrandClassifications(productBrandClassifications)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  },[ product, pageUtils ])

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={ sellerproductConvertToMarketProductFormInitialValues(product) }
      validationSchema={ sellerproductConvertToMarketProductFormSchema(brandClassifications) }
      onSubmit={ onSubmit }
    >
      {
        (formik) => (
          <Form>
            <div className='shadow-sm rounded mb-3 p-3'>
              <div className='d-flex'>
                <div style={{ maxWidth: '100px' }}>
                  <img
                    src={ product?.images[ 0 ]?.s3_url }
                    className='w-100'
                  />
                </div>
                <div className='pl-5'>
                  <div className='font-weight-bold'>{ product?.details.brand }</div>
                  <div  className='pb-3'>{ product?.name }</div>

                  <small>{ 'Tawaran Pertama:' }</small>
                  <div style={{ color: 'brown' }}>
                    { PriceUtil.formatToIDR(product?.price) }
                  </div>
                </div>
              </div>

              <CurrencyInputField
                name={ 'price_offer' }
                label={ 'Harga yang ditawarkan' }
                onKeyUp={ () => handlePriceChange(formik.values.price_offer) }
                required={ true }
              />
              <FormText>
                { 'Harga yang dipublish : ' }
                { PriceUtil.formatToIDR(revenues?.selling_price, true, 'IDR') }
              </FormText>
            </div>

            <ol className='pl-3'>
              <li value='1' className='pl-2'>
                { 'Harga yang akan dipublish di aplikasi/website Tinkerlust sudah termasuk komisi Tinkerlust dan biaya autentikasi.' }
              </li>
            </ol>
            <SellerRevenueExample revenues={ revenues }/>

            <ol className='pl-3'>
              <li
                value='2'
                className='pl-2'
              >
                { 'Jika diperlukan informasi tambahan mengenai detail produk, penjual bersedia memberikan informasi yang diminta.' }
              </li>
              <li
                value='3'
                className='pl-2'
              >
                { 'Syarat dan ketentuan metode penjualan Marketplace di bawah ini berlaku untuk setiap produk yang dijual menggunakan metode tersebut.' }
              </li>
            </ol>

            <CustomInput
              className='pb-5'
              id={ 'confirmation-convert-agreement' }
              color='secondary'
              type='checkbox'
              name='confirmation-convert-agreement'
              label={
                <p>
                  { 'Saya setuju dengan' }
                  <a href='https://www.tinkerlust.com/sell/marketplace/terms-and-conditions'>
                    { ' syarat & ketentuan ' }
                  </a>{ 'yang berlaku' }
                </p>
              }
              checked={ isAgreementChecked }
              onChange={ () => {setIsAgreementChecked(!isAgreementChecked) } }
            />

            <LoaderButton
              block={ true }
              type="submit"
              color="primary"
              disabled={ !isAgreementChecked }
              isLoading={ formik.isSubmitting }
            >
              { 'Pindahkan ke Marketplace' }
            </LoaderButton>
          </Form>
        )
      }
    </Formik>
  )
}
SellerProductConvertToMarketProductForm.propTypes    = propTypes
SellerProductConvertToMarketProductForm.defaultProps = defaultProps

export default SellerProductConvertToMarketProductForm
